import { latLng } from 'leaflet'
import config from '@/modules/base-module/config/stores/config'

const defaultZagrebCenter = latLng(45.80983713304906, 15.986897825540488)

const defaultMapOptions = {
  zoom: 7,
  zoomControl: false,
  center: defaultZagrebCenter,
  maxZoom: 19,
  /*
   * Wheel zoom: quarter-level steps, lightly coalesced.
   *
   * zoomSnap is what decides how coarse a wheel notch feels, and Leaflet's default of 1 makes a
   * small one impossible: _performZoom computes ceil(d3 / zoomSnap) * zoomSnap, so *any* movement,
   * however slight, rounds up to a whole zoom level. 0.25 quarters that. MapLibre renders fractional
   * zoom natively and Leaflet scales raster tiles for it, which two screens here already rely on
   * (MapBaseTrackingHistory uses 0 and LocationsMap 0.2). 0 removes the snapping altogether.
   *
   * zoomDelta is deliberately left at its default, so the +/- buttons and the keyboard still move a
   * full level the way they always have. 1 is a multiple of 0.25, so the two agree.
   *
   * The debounce is above Leaflet's 40ms but no longer far above it. Its purpose is that one flick
   * of a wheel becomes one zoom animation rather than a burst of them interrupting each other,
   * which the MapLibre layer pays for in full style updates - but it accumulates wheel delta while
   * it waits, and at 100ms it had accumulated enough to make each applied step two or three levels
   * at once. 60ms keeps most of the coalescing without the lumpiness. Vue2LeafletMaplibre no longer
   * breaks when animations do interrupt each other, so this is a smoothness knob now, not a fix.
   */
  zoomSnap: 0.25,
  wheelDebounceTime: 60,
  wheelPxPerZoomLevel: 120
}

/**
 * How far each kind of tile source can be zoomed, where the map type itself does not say - a type
 * may carry its own `maxZoom`/`maxNativeZoom` (see MapConfiguration.php) and that wins. Read by
 * MapTileLayers, which turns it into the options the layer is built with.
 *
 * `maxNativeZoom` is the last level the source actually has tiles for; Leaflet scales that last
 * tile for everything past it, which is why `maxZoom` may sit above it.
 *
 * - raster: our own OSM tiles and HERE, which stop at z18.
 * - google: Google's road imagery. Its satellite type goes closer and says so itself.
 * - vector: rendered client side, so it overzooms cleanly - nothing to clamp natively.
 */
const tileZoomSetup = {
  raster: {
    maxZoom: 19,
    maxNativeZoom: 18
  },
  google: {
    maxZoom: 20,
    maxNativeZoom: 20
  },
  vector: {
    maxZoom: 20
  }
}

const configState = config.state

const companyScopeGeolocation = () => {
  try {
    if (configState &&
      configState.companyScope &&
      configState.companyScope.company &&
      configState.companyScope.company.geolocation &&
      configState.companyScope.company.geolocation.length &&
      configState.companyScope.company.geolocation[0]
    ) {
      return latLng(configState.companyScope?.company?.geolocation[0])
    }
    else {
      return null
    }
  }
  catch (e) {
    console.log('Error occurred while trying to get company scope geolocation in mapConfig.js')
    return null
  }
}

const companyGeolocation = () => {
  try {
    if (configState &&
      configState.user &&
      configState.user.company &&
      configState.user.company.geolocation &&
      configState.user.company.geolocation.length &&
      configState.user.company.geolocation[0]
    ) {
      return latLng(configState.user?.company?.geolocation[0])
    }
    else {
      return null
    }
  }
  catch (e) {
    console.log('Error occurred while trying to get user geolocation in mapConfig.js')
    return null
  }
}

/**
 * Where a map should open.
 *
 * The company the user is scoped to, their own company otherwise, and Zagreb only when the config
 * carries neither. Every value behind it is read straight off the config store, so this resolves
 * synchronously and a map can be *created* already looking at the right place - rather than being
 * created looking at Zagreb and moved once it is ready, which costs a whole viewport of tiles for
 * a view nobody sees. Used by MapBase for both, so the two cannot drift apart.
 *
 * @returns {Object} Leaflet LatLng
 */
const initialMapCenter = () => companyScopeGeolocation() || companyGeolocation() || defaultZagrebCenter

/**
 * The map type a user with no saved choice is given, by key rather than by position in the toggler.
 *
 * The toggler's order is the chooser's own and leads with a vector type, which the Google Maps views
 * cannot draw at all (they render only entries with a google key or a raster `url`) - so taking the
 * first entry made a blank map the default for every new user. Kept in step with
 * MapConfiguration::DEFAULT_MAP_TYPE_KEY.
 */
const DEFAULT_MAP_TYPE_KEY = 'osm'

/**
 * The map type a view should start with: the one the user saved, the default otherwise, and
 * whatever the toggler leads with only if neither is there to be found.
 *
 * @param {Object} viewConfig  A view config as the backend reports it.
 * @returns {Object|undefined}
 */
const resolveMapType = (viewConfig) => {
  const mapTypes = viewConfig?.mapTypeToggler || []
  const byKey = (key) => mapTypes.find(mapType => mapType?.key === key)

  return byKey(viewConfig?.selected_map_type) || byKey(DEFAULT_MAP_TYPE_KEY) || mapTypes[0]
}

export {
  defaultMapOptions,
  tileZoomSetup,
  initialMapCenter,
  resolveMapType
}
